import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Handshake, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { SingleUploader } from "@/components/admin/Uploader";

const EMPTY = { name: "", category: "partenaire", website: "", logo: "", order: 0, published: true };

export default function PartnersAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/partners").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      await api.post("/admin/partners", { ...form, order: items.filter((i) => i.category === form.category).length });
      toast.success("Ajouté");
      setForm({ ...EMPTY, category: form.category });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (p) => { if (!window.confirm(`Supprimer « ${p.name} » ?`)) return; await api.delete(`/admin/partners/${p.id}`); toast.success("Supprimé"); load(); };
  const togglePublish = async (p) => { const d = { ...p, published: !p.published }; delete d.id; delete d.created_at; delete d.updated_at; await api.put(`/admin/partners/${p.id}`, d); load(); };

  const field = "w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";
  const groups = [
    { key: "partenaire", label: "Partenaires & certifications", icon: Handshake, hint: "Affichés en bandeau défilant au milieu de la page d'accueil." },
    { key: "client", label: "Clients", icon: Building2, hint: "Affichés juste au-dessus du footer (« Ils nous ont fait confiance »)." },
  ];

  return (
    <div>
      <h1 className="font-heading text-3xl font-black text-ink">Partenaires & clients</h1>
      <p className="text-ink/50 mt-1">Gérez les logos affichés sur la page d'accueil.</p>

      {/* Add form */}
      <div className="mt-6 bg-card border border-border rounded-xl p-5">
        <h2 className="font-heading font-bold text-ink mb-3">Ajouter un logo</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
          <div className="md:col-span-1">
            <label className="text-xs font-semibold text-ink/60 block mb-1">Logo (image)</label>
            <SingleUploader testid="partner-logo" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} />
          </div>
          <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-ink/60">Nom</label><input data-testid="partner-name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Ville de Marignane" /></div>
            <div><label className="text-xs font-semibold text-ink/60">Catégorie</label><select data-testid="partner-category" className={field} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}><option value="partenaire">Partenaire / Certification</option><option value="client">Client</option></select></div>
            <div className="sm:col-span-2"><label className="text-xs font-semibold text-ink/60">Site web (optionnel)</label><input className={field} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" /></div>
            <div className="sm:col-span-2">
              <button onClick={add} disabled={saving} data-testid="partner-add" className="inline-flex items-center justify-center gap-2 bg-primary text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Ajouter</button>
              <span className="ml-3 text-xs text-ink/40">Astuce : sans logo, le nom s'affiche en texte.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Lists */}
      {groups.map((g) => (
        <div key={g.key} className="mt-8">
          <div className="flex items-center gap-2 mb-1"><g.icon size={18} className="text-primary" /><h2 className="font-heading text-xl font-black text-ink">{g.label}</h2></div>
          <p className="text-xs text-ink/40 mb-4">{g.hint}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.filter((i) => i.category === g.key).map((p) => (
              <div key={p.id} className="bg-card border border-border rounded-xl p-4 flex flex-col items-center text-center" data-testid={`partner-item-${p.id}`}>
                <div className="h-16 flex items-center justify-center mb-2">
                  {p.logo ? <img src={fileUrl(p.logo)} alt={p.name} className="max-h-16 max-w-full object-contain" /> : <span className="font-heading font-black text-lg text-ink/40">{p.name}</span>}
                </div>
                <p className="text-sm font-semibold text-ink line-clamp-1">{p.name}</p>
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={() => togglePublish(p)} className={`text-[11px] px-2 py-1 rounded-full ${p.published ? "bg-green-100 text-green-700" : "bg-muted text-ink/50"}`}>{p.published ? "Visible" : "Masqué"}</button>
                  <button onClick={() => remove(p)} className="p-1.5 text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {items.filter((i) => i.category === g.key).length === 0 && <p className="text-ink/40 text-sm col-span-full">Aucun élément.</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
